'use client';

import { useToast } from '@/components/ui/use-toast';
import { ShoppingCart } from '@/icons/ShoppingCart';
import { getBasket, refreshBasketFromAPI } from '@/lib/BasketStorage';
import {
  APPOINTMENT_PRIMARY_CTA,
  formatBasketDeliveryRegion,
  getCheckoutStartPath,
} from '@/lib/basketUtils';
import { SETTINGS_TYPE } from '@/lib/Constants';
import {
  getDistrictRemediationState,
  isDistrictRemediationActive,
  markDistrictRemediationProductResolved,
  resolveDeliverySlotFromData,
  resolveRemediationDeliveryParams,
  resolveRemediationDeliveryParamsFromSelection,
} from '@/lib/DistrictRemediationStorage';
import { Endpoints } from '@/lib/Endpoints';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { StatusCode } from '@/lib/StatusCode';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { getBasketEcommerceBodyForProduct, GTMEventType } from '@/lib/GTMEventType';
import { ScreenSize, useScreenSize } from '@/lib/hook/useScreenSize';
import { getLastSelectedCity, isAppClient, setLastSelectedCity } from '@/lib/LocalStorage';
import { loadLocationsFromBackend } from '@/lib/LocationListHelper';
import { appendCampaignParamsToUrl } from '@/lib/persistCampaignParams';
import { Requester } from '@/lib/Requester';
import {
  getCities,
  getDistricts,
  getOtherCityId,
  getOtherCityTargetPath,
  loadSettings,
  presetOtherCityId,
  presetOtherCityTargetPath,
} from '@/lib/SettingsStorage';
import { syncSiteDistrictFromBasket } from '@/lib/SiteDistrictSync';
import {
  appRPC,
  calculateDiscountPercentage,
  formatBasketDeliveryDate,
  formatNumberToPriceText,
  formatToISODate,
  getCargoInfo,
  normalizeText,
} from '@/lib/utils';
import { WebLink } from '@/lib/WebLinks';
import { WindowDispatchEvent } from '@/lib/WindowDispatchEvent';
import { Basket, BasketResponseModel } from '@/models/Basket';
import { City, District, Warehouse } from '@/models/City';
import { DeliveryData, DeliveryTimeDate, DeliveryTimeSlot } from '@/models/DeliveryData';
import { GenericResponse } from '@/models/GenericResponse';
import { PriceData, Product, PRODUCT_TYPE } from '@/models/Product';
import { differenceInSeconds, parseISO } from 'date-fns';
import { AlertCircle, Calendar, Info } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import AllowedProductDialog from './AllowedProductDialog';
import CityChooserPopover from './CityChooserPopover';
import Countdown from './Countdown';
import DailyDeliveryTimer from './DailyDeliveryTimer';
import ProductOrderDateOptions from './ProductOrderDateOptions';
import SmartImage from './SmartImage';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Spinner } from './ui/spinner';
import { Ratings } from './ui/stars';

interface PageStateTypes {
  cargoDisrtictPopoverOpen: boolean;
  chooseCityVisible: boolean;
  chooseDistrictVisible: boolean;
  cities: City[];
  districts: District[];
  visibleDistricts: District[];
  selectedCity?: City;
  selectedDistrict?: District;
  deliveryData?: DeliveryData;
  selectedDeliveryDate?: DeliveryTimeDate;
  selectedDeliveryTime?: string;
  selectedDirectButton?: DeliveryTimeSlot;
  datePickerSelected: boolean;
  customDate?: Date;
  orderLoading: boolean;
  loadingDeliveryTimesId: number;
  deliveryFee?: number | null;
  otherCityId: number;
  otherCityTargetPath: string | undefined;
  sprayText1?: string;
  sprayText2?: string;
  decorationWarehouses: Warehouse[];
  selectedWarehouse?: Warehouse;
  decorationReservedByDate: Record<string, number[]>;
}

const PAGE_STATE = {
  cargoDisrtictPopoverOpen: false,
  chooseCityVisible: true,
  chooseDistrictVisible: false,
  cities: [],
  districts: [],
  visibleDistricts: [],
  selectedCity: undefined,
  selectedDistrict: undefined,
  deliveryData: undefined,
  selectedDeliveryDate: undefined,
  selectedDeliveryTime: undefined,
  customDate: undefined,
  datePickerSelected: false,
  orderLoading: false,
  loadingDeliveryTimesId: -1,
  deliveryFee: null,
  otherCityId: presetOtherCityId,
  otherCityTargetPath: presetOtherCityTargetPath,
  sprayText1: undefined,
  sprayText2: undefined,
  decorationWarehouses: [],
  decorationReservedByDate: {},
} as PageStateTypes;

type DecorationTimesApi = {
  dates: DeliveryTimeDate[];
  schemas: Array<{
    deliveryTimes: Array<{
      id: number;
      title: string;
      start: string;
      end: string;
      deadline: string;
      deliveryDuration?: number;
    }>;
  }>;
  reservedTimes: Array<{
    orderDate: string;
    deliveryTimeId?: number;
    deliveryTime?: { id: number };
  }>;
};

function buildDecorationDeliveryState(times: DecorationTimesApi): {
  deliveryData: DeliveryData;
  reservedByDate: Record<string, number[]>;
} {
  const reservedByDate: Record<string, number[]> = {};
  for (const row of times.reservedTimes || []) {
    const raw = row.orderDate as unknown as string | Date;
    const d = typeof raw === 'string' ? raw.slice(0, 10) : formatToISODate(new Date(raw));
    const slotId = row.deliveryTimeId ?? row.deliveryTime?.id;
    if (slotId === undefined || slotId === null || Number.isNaN(Number(slotId))) continue;
    if (!reservedByDate[d]) reservedByDate[d] = [];
    reservedByDate[d].push(Number(slotId));
  }
  const rawSlots = times.schemas?.[0]?.deliveryTimes || [];
  const deliveryTimes: DeliveryTimeSlot[] = rawSlots.map((s) => ({
    id: String(s.id),
    title: s.title,
    start: s.start,
    end: s.end,
    deadline: s.deadline,
  }));
  const deliveryData: DeliveryData = {
    dates: (times.dates || []).map((dt) => ({
      ...dt,
      id: dt.id || dt.date,
    })),
    schemas: [
      {
        deliveryTimes,
        directButtons: [],
        rule: {},
      },
    ],
  };
  return { deliveryData, reservedByDate };
}

export default function ProductOrderOptions({
  product,
  activeProduct,
  onActiveProductChanged,
  pricesData,
  forceCampaign,
  openReviews,
  recommendedProducts: recommendedProductsProp = [],
  selectedRecommendedIds,
  setSelectedRecommendedIds,
}: {
  product: Product;
  activeProduct: Product;
  onActiveProductChanged: Function;
  pricesData?: PriceData;
  forceCampaign?: string | undefined;
  openReviews: () => void;
  recommendedProducts?: Product[];
  selectedRecommendedIds: Set<number>;
  setSelectedRecommendedIds: React.Dispatch<React.SetStateAction<Set<number>>>;
}) {
  const [pageState, setPageState] = useState(PAGE_STATE);
  const { toast } = useToast();
  const router = useRouter();
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [cityTitle, setCityTitle] = useState<String | null>(null);
  const [deliveryIsCargo, setDeliveryIsCargo] = useState<boolean>(false);
  const [deliveryIsCargoSet, setDeliveryIsCargoSet] = useState<boolean>(false);
  const [hasDeliveryOptions, setHasDeliveryOptions] = useState<boolean>(true);
  const [allowedProductDialogVisible, setAllowedProductDialogVisible] = useState(false);
  const [cargo, setCargo] = useState(getCargoInfo());
  const [localBasket, setLocalBasket] = useState<Basket | undefined>(undefined);
  const [useSiteRegionPicker, setUseSiteRegionPicker] = useState(false);
  const [clearBasketDialogOpen, setClearBasketDialogOpen] = useState(false);
  const [clearBasketLoading, setClearBasketLoading] = useState(false);
  const screenSize = useScreenSize();

  const sprayInputRef1 = useRef<HTMLInputElement>(null);
  const sprayInputRef2 = useRef<HTMLInputElement>(null);
  const shouldScrollToDateAfterDistrictSelectionRef = useRef(false);

  const cityChooserRef = useRef<HTMLDivElement>(null);
  const dateChooserRef = useRef<HTMLDivElement>(null);
  const remediationDistrictLoadedRef = useRef(false);
  const pendingForceAddRef = useRef(false);
  const basketHasItems = (localBasket?.items?.length || 0) > 0;
  const isVehicleDecoration = activeProduct.productTypeId === PRODUCT_TYPE.VEHICLE_DECORATION;
  const showBasketDeliverySummary = basketHasItems && !isVehicleDecoration && !useSiteRegionPicker;

  useEffect(() => {
    if (!basketHasItems) {
      setUseSiteRegionPicker(false);
    }
  }, [basketHasItems]);

  useEffect(() => {
    remediationDistrictLoadedRef.current = false;
  }, [activeProduct.id]);

  useEffect(() => {
    if (isVehicleDecoration || remediationDistrictLoadedRef.current) return;

    const remediationState = getDistrictRemediationState();
    if (!remediationState?.active) return;
    if (!remediationState.pendingProductIds.includes(activeProduct.id)) return;

    const targetDistrict = pageState.districts.find(
      (district) => district.id === remediationState.targetDistrictId,
    );
    if (!targetDistrict || pageState.districts.length === 0) return;

    remediationDistrictLoadedRef.current = true;
    selectDistrict(targetDistrict, true);
  }, [isVehicleDecoration, activeProduct.id, pageState.districts]);

  useEffect(() => {
    if (!isVehicleDecoration) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: result } = await Requester.get<
          GenericResponse<{ homepage: { items: { warehouses?: Warehouse[] } } }>
        >(Endpoints.SETTINGS, {
          params: { type: SETTINGS_TYPE.LOCATIONS },
        });
        const w = result?.data?.homepage?.items?.warehouses ?? [];
        if (!cancelled) {
          setPageState((prev) => ({ ...prev, decorationWarehouses: w }));
        }
      } catch {
        if (!cancelled) {
          setPageState((prev) => ({ ...prev, decorationWarehouses: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isVehicleDecoration, activeProduct.id]);

  useEffect(() => {
    const basketListener = () => {
      setLocalBasket(getBasket());
    };

    basketListener();
    window.addEventListener(WindowDispatchEvent.UPDATE_BASKET, basketListener);

    return () => {
      window.removeEventListener(WindowDispatchEvent.UPDATE_BASKET, basketListener);
    };
  }, []);

  useEffect(() => {
    if (
      shouldScrollToDateAfterDistrictSelectionRef.current &&
      pageState.loadingDeliveryTimesId === -1 &&
      pageState.deliveryData
    ) {
      shouldScrollToDateAfterDistrictSelectionRef.current = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dateChooserRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    }
  }, [pageState.loadingDeliveryTimesId, pageState.deliveryData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCargo(getCargoInfo());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeProduct.badge?.badgeEndTime) {
      try {
        const badgeEndDate = parseISO(activeProduct.badge.badgeEndTime);
        if (differenceInSeconds(badgeEndDate, new Date()) > 10) {
          setEndDate(badgeEndDate);
        } else {
          setEndDate(null);
        }
      } catch (error) {
        console.error('Invalid date format for activeProduct badge:', activeProduct.badge);
        setEndDate(null);
      }
    } else {
      setEndDate(null);
    }
  }, [activeProduct]);

  const recommendedProducts = recommendedProductsProp;

  useEffect(() => {
    if (isVehicleDecoration) {
      const wh = pageState.selectedWarehouse;
      if (!wh || !activeProduct) {
        return;
      }
      const updateDecorationTimes = () => {
        setPageState((prev) => ({
          ...prev,
          deliveryData: undefined,
        }));
        loadDecorationDeliveryData(wh);
      };
      updateDecorationTimes();
      const interval = setInterval(updateDecorationTimes, 10 * 60 * 1000);
      return () => clearInterval(interval);
    }

    const updateDeliveryTimes = () => {
      const selectedDistrict = pageState.selectedDistrict;
      if (selectedDistrict && activeProduct) {
        setPageState((prev) => ({
          ...prev,
          deliveryData: undefined,
        }));
        loadDeliveryData(selectedDistrict);
      }
    };

    updateDeliveryTimes();
    const interval = setInterval(updateDeliveryTimes, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [
    isVehicleDecoration,
    pageState.selectedDistrict?.id,
    pageState.selectedWarehouse?.id,
    activeProduct.id,
  ]);

  useEffect(() => {
    if (isVehicleDecoration) {
      const handleFocus = () => {
        const wh = pageState.selectedWarehouse;
        if (wh && activeProduct) {
          loadDecorationDeliveryData(wh);
        }
      };
      const handleVisibilityChange = () => {
        if (!document.hidden && pageState.selectedWarehouse && activeProduct) {
          loadDecorationDeliveryData(pageState.selectedWarehouse);
        }
      };
      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }

    const handleFocus = () => {
      if (pageState.selectedDistrict && activeProduct) {
        loadDeliveryData(pageState.selectedDistrict);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && pageState.selectedDistrict && activeProduct) {
        loadDeliveryData(pageState.selectedDistrict);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    isVehicleDecoration,
    pageState.selectedDistrict?.id,
    pageState.selectedWarehouse?.id,
    activeProduct.id,
  ]);

  useEffect(() => {
    loadSettings();
  }, [product]);

  useEffect(() => {
    const updateCityAndDistrictWithLocalValue = () => {
      const localValues = getLastSelectedCity();
      setCityTitle(localValues?.city?.title?.split(' ')[0] || null);
      if (localValues && localValues.district && !isVehicleDecoration) {
        selectDistrict(localValues.district, false);
      }
    };

    const districtsListener = () => {
      setPageState((prev) => ({
        ...prev,
        districts: getDistricts(),
      }));
    };
    const citiesListener = () => {
      setPageState((prev) => ({
        ...prev,
        cities: getCities(),
      }));
    };

    window.addEventListener(
      WindowDispatchEvent.UPDATE_SELECTED_CITY,
      updateCityAndDistrictWithLocalValue,
    );

    window.addEventListener(WindowDispatchEvent.UPDATE_CITIES, citiesListener);

    window.addEventListener(WindowDispatchEvent.UPDATE_DISTRICTS, districtsListener);

    setPageState((prev) => ({
      ...prev,
      districts: getDistricts(),
      cities: getCities(),
      otherCityId: getOtherCityId(),
      otherCityTargetPath: getOtherCityTargetPath(),
    }));
    updateCityAndDistrictWithLocalValue();

    return () => {
      window.removeEventListener(
        WindowDispatchEvent.UPDATE_SELECTED_CITY,
        updateCityAndDistrictWithLocalValue,
      );

      window.removeEventListener(WindowDispatchEvent.UPDATE_DISTRICTS, districtsListener);

      window.removeEventListener(WindowDispatchEvent.UPDATE_CITIES, citiesListener);
    };
  }, [isVehicleDecoration]);

  useEffect(() => {
    async function getLocations() {
      const locations = await loadLocationsFromBackend();
      if (
        !locations ||
        !locations.cities ||
        !locations.districts ||
        locations.cities.length === 0 ||
        locations.districts.length === 0
      ) {
        return;
      }
      setPageState((prev) => ({
        ...prev,
        cities: locations.cities,
        districts: locations.districts,
      }));
    }

    if (pageState.cargoDisrtictPopoverOpen) {
      getLocations();
    }
  }, [pageState.cargoDisrtictPopoverOpen]);

  async function enableDisctrictChooser(cityId: number) {
    setPageState((prev) => ({
      ...prev,
      visibleDistricts: prev.districts.filter((district) => district.cityId === cityId),
      chooseCityVisible: false,
      chooseDistrictVisible: true,
    }));
  }

  async function selectDistrict(district: District, callDeliveryData: boolean = true) {
    let setDirectButtonFree = false;

    if (district.cityId === pageState.otherCityId) {
      setDeliveryIsCargo(true);
      setHasDeliveryOptions(false);
    } else {
      setDeliveryIsCargo(false);
      setDirectButtonFree = true;
    }
    setDeliveryIsCargoSet(true);

    setPageState((prev) => {
      const selectedCity = prev.cities.find((city) => city.id === district.cityId);
      setLastSelectedCity(selectedCity, district);

      return {
        ...prev,
        visibleDistricts: [],
        chooseCityVisible: true,
        chooseDistrictVisible: false,
        cargoDisrtictPopoverOpen: false,
        selectedDistrict: district,
        deliveryData: undefined,
        customDate: undefined,
        datePickerSelected: false,
        selectedCity: selectedCity,
        selectedDirectButton: setDirectButtonFree ? undefined : prev.selectedDirectButton,
      };
    });

    if (callDeliveryData) {
      loadDeliveryData(district, { scrollAfterLoad: true });
    }
  }

  /** Sepetteki tüm kalemleri API'den siler; ürün sayfası seçimlerini (depo/tarih vb.) değiştirmez. */
  async function clearAllBasketItemsOnly(): Promise<boolean> {
    const productIds = Array.from(new Set((localBasket?.items || []).map((item) => item.id)));
    if (productIds.length === 0) {
      return true;
    }
    try {
      for (const productId of productIds) {
        await Requester.post<GenericResponse<BasketResponseModel>>(Endpoints.BASKET_REMOVE_ITEM, {
          productId,
        });
      }
      await refreshBasketFromAPI('ProductOrderOptions - clearAllBasketItemsOnly');
      return true;
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
      return false;
    }
  }

  function switchToSiteRegionPicker() {
    const localValues = getLastSelectedCity();
    setUseSiteRegionPicker(true);

    if (localValues?.district) {
      selectDistrict(localValues.district, true);
      return;
    }

    setPageState((prev) => ({
      ...prev,
      selectedCity: localValues?.city,
      selectedDistrict: undefined,
      selectedDeliveryDate: undefined,
      selectedDeliveryTime: undefined,
      selectedDirectButton: undefined,
      deliveryData: undefined,
      customDate: undefined,
      datePickerSelected: false,
      chooseCityVisible: !localValues?.city,
      chooseDistrictVisible: Boolean(localValues?.city),
      visibleDistricts: localValues?.city
        ? prev.districts.filter((district) => district.cityId === localValues.city?.id)
        : [],
    }));
  }

  async function confirmClearBasketForSelectionChange() {
    if (clearBasketLoading) return;

    setClearBasketLoading(true);
    try {
      const cleared = await clearAllBasketItemsOnly();
      if (!cleared) {
        return;
      }
      pendingForceAddRef.current = true;
      setClearBasketDialogOpen(false);
      switchToSiteRegionPicker();
    } finally {
      setClearBasketLoading(false);
    }
  }

  /** Sepetteki tüm kalemleri API'den siler; ürün sayfası seçimlerini (depo/tarih vb.) değiştirmez. */
  function resolvePendingRemediationAddParams() {
    const remediationState = getDistrictRemediationState();
    if (
      !remediationState?.active ||
      !remediationState.pendingProductIds.includes(activeProduct.id)
    ) {
      return null;
    }

    return (
      resolveRemediationDeliveryParams(pageState.deliveryData) ??
      resolveRemediationDeliveryParamsFromSelection({
        districtId: pageState.selectedDistrict?.id,
        date:
          pageState.selectedDeliveryDate?.date ||
          (pageState.customDate ? formatToISODate(pageState.customDate) : undefined) ||
          remediationState.lockedDate,
        deliveryTimeId:
          pageState.selectedDirectButton?.id ?? pageState.selectedDeliveryTime ?? undefined,
      })
    );
  }

  async function onOrderClick() {
    const isDecoration = activeProduct.productTypeId === PRODUCT_TYPE.VEHICLE_DECORATION;
    const remediationState = getDistrictRemediationState();
    const remediationActive = isDistrictRemediationActive();
    const isPendingRemediationProduct = Boolean(
      remediationState?.pendingProductIds.includes(activeProduct.id),
    );
    const remediationAddParams = isPendingRemediationProduct
      ? resolvePendingRemediationAddParams()
      : null;

    if (isPendingRemediationProduct && !remediationAddParams) {
      toast({
        variant: 'destructive',
        description:
          pageState.loadingDeliveryTimesId >= 0
            ? 'Teslimat bilgileri yükleniyor, lütfen bekleyin.'
            : 'Teslimat saati bu mahalle için hazırlanamadı. Lütfen sayfayı yenileyip tekrar deneyin.',
      });
      return;
    }

    const pageDistrictId = pageState.selectedDistrict?.id;
    const pageDate = pageState.selectedDirectButton
      ? formatToISODate(new Date())
      : pageState.selectedDeliveryDate?.date ||
        (pageState.customDate ? formatToISODate(pageState.customDate) : undefined);
    const pageDeliveryTimeId = Number(
      pageState.selectedDirectButton?.id
        ? pageState.selectedDirectButton?.id
        : pageState.selectedDeliveryTime,
    );

    // useSiteRegionPicker: kullanıcı teslimat özetini değiştirip yeni seçim yaptı → pageState kullan.
    // Sepette ürün varken ve değiştirilmediyse mevcut basket ayarlarını koru.
    const usePageDeliverySelection = isDecoration || useSiteRegionPicker || !basketHasItems;

    const { productId, districtId, date, deliveryTimeId } = {
      productId: activeProduct.id,
      districtId: isDecoration
        ? undefined
        : remediationAddParams
          ? remediationAddParams.districtId
          : usePageDeliverySelection
            ? pageDistrictId
            : localBasket?.district?.id,
      date: isDecoration
        ? pageDate
        : remediationAddParams
          ? remediationAddParams.date
          : usePageDeliverySelection
            ? pageDate
            : localBasket?.deliveryTime?.date,
      deliveryTimeId: isDecoration
        ? pageDeliveryTimeId
        : remediationAddParams
          ? remediationAddParams.deliveryTimeId
          : usePageDeliverySelection
            ? pageDeliveryTimeId
            : Number(localBasket?.deliveryTime?.id),
    };

    const warehouseId = isDecoration ? pageState.selectedWarehouse?.id : undefined;

    if (productId === undefined || productId === null) {
      toast({
        variant: 'destructive',
        description: 'Bilinmeyen bir hata oluştu',
      });
      return;
    }

    if (isDecoration) {
      if (warehouseId === undefined || warehouseId === null) {
        toast({
          variant: 'destructive',
          description: 'Lütfen araç süsleme noktası (depo) seçiniz.',
        });
        return;
      }
    } else if (districtId === undefined || districtId === null) {
      setPageState((prev) => ({
        ...prev,
        cargoDisrtictPopoverOpen: true,
      }));
      return;
    }

    if (!date || date === '') {
      toast({
        variant: 'destructive',
        description: isDecoration
          ? 'Lütfen randevu tarihi seçiniz.'
          : 'Lütfen gönderim tarihi seçiniz.',
      });

      if (dateChooserRef.current) {
        dateChooserRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    if (
      activeProduct.productTypeId === PRODUCT_TYPE.SPRAY &&
      (!pageState.sprayText1 || pageState.sprayText1 === '')
    ) {
      toast({
        variant: 'destructive',
        description: 'Lütfen çelenk şerit yazısı girin.',
      });

      if (sprayInputRef1.current) {
        sprayInputRef1.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sprayInputRef1.current.focus();
      }
      return;
    }

    if (deliveryTimeId === undefined || deliveryTimeId === null || isNaN(deliveryTimeId)) {
      toast({
        variant: 'destructive',
        description: isDecoration
          ? 'Lütfen randevu saati seçiniz.'
          : 'Lütfen gönderim saati seçiniz.',
      });

      if (dateChooserRef.current) {
        dateChooserRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // "Değiştir" sonrası yeni tarih/bölge/saat seçildiyse eski sepet ayarlarını temizle.
    // Aksi halde add-item eski basket.deliveryTime ile gider (veya saat değişmez).
    const deliverySelectionChanged =
      !isDecoration &&
      useSiteRegionPicker &&
      basketHasItems &&
      (Number(localBasket?.district?.id) !== Number(districtId) ||
        localBasket?.deliveryTime?.date !== date ||
        Number(localBasket?.deliveryTime?.id) !== Number(deliveryTimeId));

    try {
      setPageState((prev) => ({
        ...prev,
        orderLoading: true,
      }));

      if ((isDecoration || deliverySelectionChanged) && basketHasItems) {
        const cleared = await clearAllBasketItemsOnly();
        if (!cleared) {
          return;
        }
        pendingForceAddRef.current = true;
      }

      const forceAdd = pendingForceAddRef.current ? 1 : 0;

      const body = isDecoration
        ? {
            productId,
            warehouseId,
            date,
            deliveryTimeId,
            forceAdd,
            itemData:
              activeProduct.productTypeId === PRODUCT_TYPE.SPRAY
                ? {
                    sprayText1: pageState.sprayText1,
                    sprayText2: pageState.sprayText2,
                    forceCampaign,
                  }
                : {
                    forceCampaign,
                  },
          }
        : {
            productId,
            districtId,
            date,
            deliveryTimeId,
            forceAdd,
            itemData:
              activeProduct.productTypeId === PRODUCT_TYPE.SPRAY
                ? {
                    sprayText1: pageState.sprayText1,
                    sprayText2: pageState.sprayText2,
                    forceCampaign,
                  }
                : {
                    forceCampaign,
                  },
          };

      const postAdd = (addForce: number) =>
        Requester.post<GenericResponse<BasketResponseModel>>(Endpoints.BASKET_ADD_ITEM, {
          ...body,
          forceAdd: addForce,
        });

      let addResult: GenericResponse<BasketResponseModel>;
      try {
        ({ data: addResult } = await postAdd(forceAdd));
      } catch (addError) {
        const { statusCode } = ErrorHandler.getMessage(addError);
        if (statusCode === StatusCode.BASKET_SETTINGS_MISMATCH && forceAdd !== 1) {
          await refreshBasketFromAPI('ProductOrderOptions - mismatch retry');
          const latest = getBasket();
          if (!latest?.items?.length) {
            ({ data: addResult } = await postAdd(1));
          } else {
            throw addError;
          }
        } else {
          throw addError;
        }
      }

      pendingForceAddRef.current = false;

      const result = addResult;

      sendGTMEvent({
        event: GTMEventType.ADD_TO_CART,
        ecommerce: getBasketEcommerceBodyForProduct(
          result.data.basket?.items.filter((item) => item.id === productId) || [],
          result.data.basket?.total || 0,
        ),
      });

      const selectedIds = Array.from(selectedRecommendedIds);
      const selectedProducts = recommendedProducts.filter((p) => selectedRecommendedIds.has(p.id));
      const validSelectedIds = selectedProducts
        .filter((p) => p.inStock === 1 && p.productTypeId !== PRODUCT_TYPE.SPRAY)
        .map((p) => p.id);

      if (validSelectedIds.length > 0 && !remediationActive) {
        const addExtraBodyBase = isDecoration
          ? {
              warehouseId,
              date,
              deliveryTimeId,
              forceAdd: 0,
              itemData: { forceCampaign },
            }
          : {
              districtId,
              date,
              deliveryTimeId,
              forceAdd: 0,
              itemData: { forceCampaign },
            };

        await validSelectedIds.reduce(async (prev, extraProductId) => {
          await prev;
          await Requester.post<GenericResponse<BasketResponseModel>>(Endpoints.BASKET_ADD_ITEM, {
            productId: extraProductId,
            ...addExtraBodyBase,
          });
        }, Promise.resolve());

        // cleanup selections once queued into basket
        setSelectedRecommendedIds(new Set());
      } else if (selectedIds.length > 0) {
        // selected but not addable (e.g. out of stock)
        setSelectedRecommendedIds(new Set());
      }

      await refreshBasketFromAPI('ProductOrderOptions - onOrderClick');

      if (isPendingRemediationProduct) {
        markDistrictRemediationProductResolved(activeProduct.id);
        const updatedBasket = getBasket();
        if (updatedBasket && !isDistrictRemediationActive()) {
          syncSiteDistrictFromBasket(updatedBasket);
        }
        router.push(appendCampaignParamsToUrl(WebLink.CART));
        return;
      }

      const checkoutPath = getCheckoutStartPath(getBasket() ?? result.data.basket);
      const checkoutPathWithCampaign = appendCampaignParamsToUrl(checkoutPath);
      const isApp = isAppClient();

      if (isApp) {
        const RPCcheck = appRPC('route', 'goFullscreen', checkoutPathWithCampaign);
        if (!RPCcheck && window) {
          window.location.href = checkoutPathWithCampaign;
          return;
        } else if (RPCcheck) {
          return;
        }
      }
      router.push(checkoutPathWithCampaign);
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        orderLoading: false,
      }));
    }
  }

  async function fetchDeliveryFeeInfo(deliveryTimeId: number) {
    try {
      const res = await fetch(`/api/delivery-fee-info?deliveryTimeId=${deliveryTimeId}`, {
        method: 'GET',
        cache: 'no-store',
      });
      if (!res.ok) {
        setPageState((prev) => ({ ...prev, deliveryFee: null }));
        return;
      }
      const json = await res.json();
      const fee = Number(json?.data?.deliveryFee ?? 0);
      setPageState((prev) => ({ ...prev, deliveryFee: fee }));
    } catch {
      setPageState((prev) => ({ ...prev, deliveryFee: null }));
    }
  }

  async function loadDeliveryData(district: District, options?: { scrollAfterLoad?: boolean }) {
    if (activeProduct.inStock === 0) return;

    if (options?.scrollAfterLoad) {
      shouldScrollToDateAfterDistrictSelectionRef.current =
        screenSize === ScreenSize.SM && district.cityId !== pageState.otherCityId;
    }

    setPageState((prev) => ({
      ...prev,
      loadingDeliveryTimesId: district.id,
    }));

    try {
      const timestamp = Date.now();
      const randomParam = Math.random().toString(36).substring(7);

      const deliveryTimes = await fetch(
        `/api/delivery-times?productId=${activeProduct.id}&districtId=${district.id}&_t=${timestamp}&_r=${randomParam}`,
        {
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      );

      if (deliveryTimes.ok) {
        const deliveryTimesData: GenericResponse<DeliveryData> = await deliveryTimes.json();
        const remediationState = getDistrictRemediationState();
        const remediationSlot =
          remediationState?.active && remediationState.pendingProductIds.includes(activeProduct.id)
            ? resolveDeliverySlotFromData(
                deliveryTimesData.data,
                remediationState.lockedDate,
                remediationState.lockedDeliveryTimeTitle,
                remediationState.lockedDeliveryTimeStart,
                remediationState.lockedDeliveryTimeEnd,
              )
            : null;

        setPageState((prev) => ({
          ...prev,
          deliveryData: deliveryTimesData.data,
          selectedDeliveryDate: remediationSlot
            ? deliveryTimesData.data?.dates?.find((item) => item.date === remediationSlot.date)
            : undefined,
          selectedDeliveryTime: remediationSlot
            ? String(remediationSlot.deliveryTimeId)
            : undefined,
        }));

        if (district.cityId === pageState.otherCityId) {
          setDeliveryIsCargo(true);
          setHasDeliveryOptions(false);
          const directButton = deliveryTimesData?.data?.schemas?.find(
            (s) => (s.directButtons?.length || 0) > 0,
          )?.directButtons?.[0];
          if (directButton) {
            setPageState((prev) => ({
              ...prev,
              selectedDirectButton: directButton,
            }));
          }
        } else {
          setDeliveryIsCargo(false);
          setPageState((prev) => ({
            ...prev,
            selectedDirectButton: undefined,
          }));
        }
        setDeliveryIsCargoSet(true);
        const feeSlotId =
          remediationSlot?.deliveryTimeId ??
          Number(deliveryTimesData?.data?.schemas?.[0]?.deliveryTimes?.[0]?.id);
        if (feeSlotId) {
          void fetchDeliveryFeeInfo(feeSlotId);
        } else {
          setPageState((prev) => ({ ...prev, deliveryFee: null }));
        }
      }
    } catch (error) {
      shouldScrollToDateAfterDistrictSelectionRef.current = false;
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        loadingDeliveryTimesId: -1,
      }));
    }
  }

  async function loadDecorationDeliveryData(
    warehouse: Warehouse,
    options?: { scrollAfterLoad?: boolean },
  ) {
    if (activeProduct.inStock === 0) return;

    if (options?.scrollAfterLoad) {
      shouldScrollToDateAfterDistrictSelectionRef.current = screenSize === ScreenSize.SM;
    }

    setPageState((prev) => ({
      ...prev,
      loadingDeliveryTimesId: warehouse.id,
    }));

    try {
      const { data: result } = await Requester.get<GenericResponse<{ times: DecorationTimesApi }>>(
        Endpoints.GET_DECORATION_TIMES,
        {
          params: { warehouseId: warehouse.id },
        },
      );
      const times = result?.data?.times;
      if (!times?.dates?.length || !times?.schemas?.[0]?.deliveryTimes?.length) {
        throw new Error('Müsait teslimat saati bulunamadı.');
      }
      const { deliveryData, reservedByDate } = buildDecorationDeliveryState(times);
      setPageState((prev) => ({
        ...prev,
        deliveryData,
        decorationReservedByDate: reservedByDate,
        selectedDeliveryDate: undefined,
        selectedDeliveryTime: undefined,
        selectedDirectButton: undefined,
        customDate: undefined,
        datePickerSelected: false,
      }));
      setDeliveryIsCargo(false);
      setHasDeliveryOptions(true);
      setDeliveryIsCargoSet(true);
    } catch (error) {
      shouldScrollToDateAfterDistrictSelectionRef.current = false;
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message || 'Teslimat saatleri yüklenemedi.',
      });
      setHasDeliveryOptions(false);
    } finally {
      setPageState((prev) => ({
        ...prev,
        loadingDeliveryTimesId: -1,
      }));
    }
  }

  async function searchDistrict(query: string) {
    if (query.length < 2) {
      setPageState((prev) => ({
        ...prev,
        chooseCityVisible: true,
        chooseDistrictVisible: false,
        visibleDistricts: [],
      }));
    } else {
      const normalizedQuery = normalizeText(query);
      setPageState((prev) => {
        const cities = prev.cities
          .filter((city) => normalizeText(city.title).includes(normalizedQuery))
          .map((city) => city.id);
        const districts = prev.districts.filter(
          (district) =>
            normalizeText(district.title).includes(normalizedQuery) ||
            cities.includes(district.cityId),
        );
        return {
          ...prev,
          chooseCityVisible: false,
          chooseDistrictVisible: true,
          visibleDistricts: districts,
        };
      });
    }
  }

  const setSprayText1 = (value: string) => {
    setPageState((prev) => ({
      ...prev,
      sprayText1: value,
    }));
  };

  const setSprayText2 = (value: string) => {
    setPageState((prev) => ({
      ...prev,
      sprayText2: value,
    }));
  };

  return (
    <div className="flex flex-col w-full gap-2 h-full relative">
      <div className="flex flex-row justify-between items-center bg-white border-[#EDEDF0] w-full rounded-xl px-4 py-4 relative">
        <div className="flex flex-col w-full">
          <div
            className={`flex flex-row justify-start sm:gap-4 ${pricesData?.[activeProduct?.id]?.badge && endDate !== null ? 'lg:max-w-[18.75rem] flex-wrap' : ''}`}
          >
            <h1 className="text-[.875rem] sm:text-[1.125rem] md:text-[1.5rem] lg:text-[2rem] text-[#212121] font-semibold font-fraunces">
              {activeProduct?.title || ''}
            </h1>
            {deliveryIsCargo &&
            pageState.loadingDeliveryTimesId === -1 &&
            pageState.selectedDirectButton ? (
              cargo.cargoToday ? (
                <div className="bg-[#D2F25C70] rounded-xl flex-row items-center gap-2 px-2 py-1 hidden sm:flex">
                  <SmartImage
                    src="/images/fast.svg"
                    width={21}
                    height={13}
                    imageType="icon"
                    className="h-4 w-auto"
                    alt="Hızlı Teslimat"
                  />
                  <div className="font-semibold text-[.6875rem] leading-[1rem] tracking-[-0.005em] text-center align-middle font-poppins">
                    {cargo.cargoDescription}
                  </div>
                </div>
              ) : (
                <div className="bg-[#D2F25C70] rounded-xl flex flex-row items-center gap-2 px-2 py-1">
                  <SmartImage
                    src="/images/fast.svg"
                    width={21}
                    height={13}
                    imageType="icon"
                    className="h-4 w-auto"
                    alt="Hızlı Teslimat"
                  />
                  <div className="font-semibold text-[1rem] leading-[1rem] tracking-[-0.005em] text-center align-middle font-poppins">
                    {cargo.cargoDescription}
                  </div>
                </div>
              )
            ) : (
              deliveryIsCargoSet &&
              pageState.loadingDeliveryTimesId === -1 &&
              pageState.deliveryFee === 0 && (
                <div className="flex flex-col items-end gap-2 flex-1">
                  {!deliveryIsCargo && <DailyDeliveryTimer className="flex" />}
                  <div
                    className={`bg-[#D2F25C] rounded-xl flex flex-row items-center gap-1 px-1 sm:px-2 py-1 whitespace-nowrap flex-shrink-0`}
                  >
                    <SmartImage
                      src="/images/free_delivery.svg"
                      width={12}
                      height={12}
                      imageType="icon"
                      className="h-3 sm:h-3 flex-shrink-0 ml-1 w-auto"
                      alt="Ücretsiz Teslimat"
                    />
                    <div className="font-bold text-[.5rem] sm:text-[.6rem] leading-[1rem] tracking-[-0.005em] text-center align-middle font-fira_mono text-[#2B3280] whitespace-nowrap">
                      Ücretsiz Teslimat&nbsp;
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
          <div className="flex flex-row justify-between mt-6">
            <div className="flex flex-row gap-2 items-center">
              {typeof pricesData?.[activeProduct?.id]?.priceBeforeDiscount === 'number' &&
                pricesData?.[activeProduct?.id]?.priceBeforeDiscount !== activeProduct?.price &&
                (pricesData?.[activeProduct?.id]?.priceBeforeDiscount || 0) > 0 &&
                calculateDiscountPercentage(
                  pricesData?.[activeProduct?.id]?.price || 0,
                  pricesData?.[activeProduct?.id]?.priceBeforeDiscount || 0,
                ) > 10 &&
                (calculateDiscountPercentage(
                  pricesData?.[activeProduct?.id]?.price || 0,
                  pricesData?.[activeProduct?.id]?.priceBeforeDiscount || 0,
                ) === 100 ? (
                  <div className="flex flex-col size-[3.5rem] items-center justify-center rounded-[1.0625rem] bg-[#2B3280]">
                    <span className="font-bold text-[1rem] text-[#BFCD8A] font-fraunces leading-[1.2]">
                      -%
                      {calculateDiscountPercentage(
                        pricesData?.[activeProduct?.id]?.price || 0,
                        pricesData?.[activeProduct?.id]?.priceBeforeDiscount || 0,
                      )}
                    </span>
                    <span className="font-bold text-[.625rem] text-[#BFCD8A] font-fraunces leading-[1.2]">
                      indirim
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col size-[3.5rem] items-center justify-center rounded-[1.0625rem] bg-[#2B3280]">
                    <span className="font-bold text-[1.25rem] text-[#BFCD8A] font-fraunces leading-[1.2]">
                      -%
                      {calculateDiscountPercentage(
                        pricesData?.[activeProduct?.id]?.price || 0,
                        pricesData?.[activeProduct?.id]?.priceBeforeDiscount || 0,
                      )}
                    </span>
                    <span className="font-bold text-[.625rem] text-[#BFCD8A] font-fraunces leading-[1.2]">
                      indirim
                    </span>
                  </div>
                ))}
              {pricesData?.[activeProduct?.id]?.price === 0 ? (
                <span className="text-[1.5rem] sm:text-[1.875rem] md:text-[2.25rem] lg:text-[2.5rem]/9 text-[#2B3280] font-bold font-fraunces">
                  ÜCRETSİZ
                </span>
              ) : (
                <div className="flex flex-col">
                  {typeof pricesData?.[activeProduct?.id]?.priceBeforeDiscount === 'number' &&
                    pricesData?.[activeProduct?.id]?.priceBeforeDiscount !== activeProduct?.price &&
                    (pricesData?.[activeProduct?.id]?.priceBeforeDiscount || 0) > 0 && (
                      <div className="text-[.875rem] sm:text-[1rem] md:text-[1.25rem] lg:text-[1.5rem]/9 font-medium text-[#5C5C57] relative w-fit font-poppins">
                        <div className="absolute w-full h-0.5 bg-[#8D8D89] rounded-full bottom-1/2 " />
                        {formatNumberToPriceText(
                          pricesData?.[activeProduct?.id]?.priceBeforeDiscount || 0,
                        )}{' '}
                        TL
                      </div>
                    )}
                  <span className="text-[1.5rem] sm:text-[1.875rem] md:text-[2.25rem] lg:text-[2.5rem]/9 text-[#2B3280] font-bold font-fraunces">
                    {pricesData?.[activeProduct?.id]?.price ? (
                      <>{formatNumberToPriceText(pricesData?.[activeProduct?.id]?.price || 0)} TL</>
                    ) : (
                      <>&nbsp;</>
                    )}
                  </span>
                </div>
              )}
            </div>

            {activeProduct.reviewData && (
              <button
                type="button"
                onClick={openReviews}
                className="hidden lg:flex flex-col items-center self-end cursor-pointer"
                aria-label="Yorumları göster"
              >
                <Ratings
                  rating={activeProduct.reviewData.averageRating}
                  onRatingChange={(_) => {}}
                  variant="yellow"
                  disabled={true}
                />
                <span className="text-sm text-[#5C5C57] underline">
                  {activeProduct.reviewData.reviewCount} değerlendirme
                </span>
              </button>
            )}
          </div>
          {deliveryIsCargo && cargo.cargoToday && (
            <div className="bg-[#D2F25C70] rounded-xl flex flex-row items-center gap-2 px-2 py-1 sm:hidden mt-4">
              <SmartImage
                src="/images/fast.svg"
                width={21}
                height={13}
                imageType="icon"
                className="h-4 w-auto"
                alt="Hızlı Teslimat"
              />
              <div className="font-semibold text-[.6875rem] leading-[1rem] tracking-[-0.005em] text-center align-middle font-poppins">
                {cargo.cargoDescription}
              </div>
            </div>
          )}
        </div>
        {pricesData?.[activeProduct?.id]?.badge && endDate !== null && (
          <div
            className="max-sm:hidden"
            style={{ position: 'absolute', right: '.625rem', top: '.625rem' }}
          >
            <Countdown endDate={endDate} transformOrigin="top right" textColor="#2B3280" />
          </div>
        )}
      </div>
      {pricesData?.[activeProduct?.id]?.badge && endDate !== null && (
        <div className="sm:hidden flex justify-center items-center bg-hzc_purple_100 rounded-xl h-20 pt-8">
          <Countdown endDate={endDate} transformOrigin="top center" textColor="#2B3280" />
        </div>
      )}

      {product.otherOptions && product.otherOptions.length > 0 && (
        <div
          key={product.id}
          className="flex flex-row bg-hzc_purple_100 py-2 px-4 rounded-lg justify-between gap-4 hover:cursor-pointer"
          onClick={() => onActiveProductChanged(product)}
        >
          <div className="flex flex-row items-center gap-2">
            <div
              className={`border-2 ${
                activeProduct?.id === product.id ? 'border-hzc_purple_500' : 'border-hzc_beige_700'
              } w-5 h-5 rounded-full justify-center content-center`}
            >
              {activeProduct?.id === product.id && (
                <div className="w-3 h-3 rounded-full bg-hzc_purple_500 mx-auto my-auto" />
              )}
            </div>
            <span className="text-hzc_purple_500 text-xs sm:text-sm md:text-lg font-medium">
              {product.title}
            </span>
          </div>

          <span className="text-hzc_purple_500 text-sm sm:text-lg md:text-lg lg:text-xl font-bold text-end">
            {formatNumberToPriceText(product.price)} TL
          </span>
        </div>
      )}
      {product.otherOptions &&
        product.otherOptions.map((option) => (
          <div
            key={option.id}
            className="flex flex-row bg-hzc_purple_100 py-2 px-4 rounded-lg justify-between gap-4 hover:cursor-pointer"
            onClick={() => onActiveProductChanged(option)}
          >
            <div className="flex flex-row items-center gap-2">
              <div
                className={`border-2 ${
                  activeProduct?.id === option.id ? 'border-hzc_purple_500' : 'border-hzc_beige_700'
                } w-5 h-5 rounded-full justify-center content-center`}
              >
                {activeProduct?.id === option.id && (
                  <div className="w-3 h-3 rounded-full bg-hzc_purple_500 mx-auto my-auto" />
                )}
              </div>
              <span className="text-hzc_purple_500 text-xs sm:text-sm md:text-lg font-medium">
                {option.title}
              </span>
            </div>
            <span className="text-hzc_purple_500 text-sm sm:text-lg md:text-lg lg:text-xl font-bold text-end">
              {formatNumberToPriceText(option.price)} TL
            </span>
          </div>
        ))}
      {activeProduct.inStock === 1 ? (
        <div className="flex flex-col gap-2" ref={cityChooserRef}>
          {showBasketDeliverySummary ? (
            <AlertDialog
              open={clearBasketDialogOpen}
              onOpenChange={(open) => {
                if (clearBasketLoading) return;
                setClearBasketDialogOpen(open);
              }}
            >
              <div className="rounded-xl border border-[#EDEDF0] bg-white p-4 flex flex-col gap-3">
                <div className="text-sm font-medium text-[#212121]">Teslimat Özeti</div>
                <div className="text-sm text-[#5C5C57]">
                  Lokasyon: {formatBasketDeliveryRegion(localBasket)}
                </div>
                <div className="text-sm text-[#5C5C57]">
                  Tarih/Saat:{' '}
                  {localBasket?.deliveryTime
                    ? formatBasketDeliveryDate(localBasket.deliveryTime)
                    : '-'}
                </div>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="outline" className="w-fit">
                    Değiştir
                  </Button>
                </AlertDialogTrigger>
              </div>
              <AlertDialogContent className="z-[200] bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Lokasyon ve tarihi değiştirmek istiyor musunuz?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Değişiklik yapmak için sepetinizdeki ürünler silinecek. Emin misiniz?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setClearBasketDialogOpen(false)}
                    disabled={clearBasketLoading}
                  >
                    İptal
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => confirmClearBasketForSelectionChange()}
                    disabled={clearBasketLoading}
                  >
                    {clearBasketLoading && <Spinner className="size-4 text-white mr-1" />}
                    Sil
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : isVehicleDecoration ? (
            <>
              <div className="rounded-xl border border-[#EDEDF0] bg-white p-4 flex flex-col gap-2">
                <div className="text-sm font-medium text-[#212121]">
                  Araç süsleme noktası (depo)
                </div>
                <Select
                  value={pageState.selectedWarehouse ? String(pageState.selectedWarehouse.id) : ''}
                  onValueChange={(value) => {
                    const w = pageState.decorationWarehouses.find((x) => String(x.id) === value);
                    if (!w) return;
                    setPageState((prev) => ({ ...prev, selectedWarehouse: w }));
                    loadDecorationDeliveryData(w, { scrollAfterLoad: true });
                  }}
                >
                  <SelectTrigger className="w-full border-hzc_beige_300 bg-white">
                    <SelectValue placeholder="Depo seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {pageState.decorationWarehouses.map((w) => (
                      <SelectItem key={w.id} value={String(w.id)}>
                        {w.title}
                        {w.address ? ` — ${w.address}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!deliveryIsCargo && (
                <ProductOrderDateOptions
                  dateChooserRef={dateChooserRef}
                  schemas={pageState.deliveryData?.schemas}
                  deliveryDates={pageState.deliveryData?.dates}
                  disabledDates={pageState.deliveryData?.disabledDates}
                  deliveryDatesLoading={pageState.loadingDeliveryTimesId > 0}
                  isLocationSelected={pageState.selectedWarehouse !== undefined}
                  selectedDeliveryDate={pageState.selectedDeliveryDate}
                  hasDeliveryOptionsHandler={(status) => setHasDeliveryOptions(status)}
                  onDeliveryDateChange={(deliveryDate) =>
                    setPageState((prev) => ({
                      ...prev,
                      selectedDeliveryDate: deliveryDate,
                      selectedDeliveryTime: undefined,
                      datePickerSelected: false,
                    }))
                  }
                  selectedDeliveryTime={pageState.selectedDeliveryTime}
                  onDeliveryTimeChange={(data) =>
                    setPageState((prev) => ({
                      ...prev,
                      selectedDeliveryTime: data,
                    }))
                  }
                  selectedDirectButton={pageState.selectedDirectButton}
                  onDirectButtonChange={(direct) =>
                    setPageState((prev) => ({
                      ...prev,
                      selectedDirectButton: direct,
                    }))
                  }
                  datePickerSelected={pageState.datePickerSelected}
                  onDatePickerSelected={(selected) =>
                    setPageState((prev) => ({
                      ...prev,
                      datePickerSelected: selected,
                      selectedDeliveryDate: undefined,
                      selectedDeliveryTime: undefined,
                      customDate: undefined,
                    }))
                  }
                  customDate={pageState.customDate}
                  onCustomDateChange={(date) =>
                    setPageState((prev) => ({
                      ...prev,
                      customDate: date,
                    }))
                  }
                  decorationReservedByDate={pageState.decorationReservedByDate}
                  useAppointmentLabels
                />
              )}
            </>
          ) : (
            <>
              <CityChooserPopover
                isOpen={pageState.cargoDisrtictPopoverOpen}
                chooseDistrictVisible={pageState.chooseDistrictVisible}
                cities={pageState.cities}
                visibleDistricts={pageState.visibleDistricts}
                closeDistrictChooser={() => searchDistrict('')}
                onOpenChange={(open: boolean) =>
                  setPageState((prev) => ({
                    ...prev,
                    cargoDisrtictPopoverOpen: open,
                  }))
                }
                searchDistrict={searchDistrict}
                triggerText={
                  pageState.selectedDistrict && pageState.selectedCity
                    ? `${pageState.selectedCity.title}, ${pageState.selectedDistrict.title}`
                    : 'Gönderim Bölgesi Seçin'
                }
                selectDistrict={selectDistrict}
                enableDisctrictChooser={enableDisctrictChooser}
              />

              {!deliveryIsCargo && (
                <ProductOrderDateOptions
                  dateChooserRef={dateChooserRef}
                  schemas={pageState.deliveryData?.schemas}
                  deliveryDates={pageState.deliveryData?.dates}
                  disabledDates={pageState.deliveryData?.disabledDates}
                  deliveryDatesLoading={pageState.loadingDeliveryTimesId > 0}
                  isLocationSelected={pageState.selectedDistrict !== undefined}
                  selectedDeliveryDate={pageState.selectedDeliveryDate}
                  hasDeliveryOptionsHandler={(status) => setHasDeliveryOptions(status)}
                  onDeliveryDateChange={(deliveryDate) =>
                    setPageState((prev) => ({
                      ...prev,
                      selectedDeliveryDate: deliveryDate,
                      selectedDeliveryTime: undefined,
                      datePickerSelected: false,
                    }))
                  }
                  selectedDeliveryTime={pageState.selectedDeliveryTime}
                  onDeliveryTimeChange={(data) =>
                    setPageState((prev) => ({
                      ...prev,
                      selectedDeliveryTime: data,
                    }))
                  }
                  selectedDirectButton={pageState.selectedDirectButton}
                  onDirectButtonChange={(direct) =>
                    setPageState((prev) => ({
                      ...prev,
                      selectedDirectButton: direct,
                    }))
                  }
                  datePickerSelected={pageState.datePickerSelected}
                  onDatePickerSelected={(selected) =>
                    setPageState((prev) => ({
                      ...prev,
                      datePickerSelected: selected,
                      selectedDeliveryDate: undefined,
                      selectedDeliveryTime: undefined,
                      customDate: undefined,
                    }))
                  }
                  customDate={pageState.customDate}
                  onCustomDateChange={(date) =>
                    setPageState((prev) => ({
                      ...prev,
                      customDate: date,
                    }))
                  }
                />
              )}
            </>
          )}

          {pageState.loadingDeliveryTimesId === -1 &&
            !hasDeliveryOptions &&
            !pageState.selectedDirectButton &&
            deliveryIsCargo && (
              <>
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <span className="text-black">
                      Bu ürünü şimdilik seçtiğiniz bölgeye teslim edemiyoruz.
                    </span>{' '}
                    {pageState.otherCityTargetPath && (
                      <a
                        className="underline cursor-pointer"
                        onClick={() => setAllowedProductDialogVisible(true)}
                      >
                        Uygun ürünleri görmek için tıklayın.
                      </a>
                    )}
                  </AlertDescription>
                </Alert>
                {pageState.otherCityTargetPath && (
                  <AllowedProductDialog
                    visible={allowedProductDialogVisible}
                    onClose={() => setAllowedProductDialogVisible(false)}
                    allowedProductsData={{
                      slug: pageState.otherCityTargetPath.replace('/', ''),
                      dialogWarningText: 'bu bölgeye teslim edemiyoruz.',
                      productsTitle: 'Kargo ile gönderim yapılabilen ürünler',
                      productsLinkText: 'Tüm ürünleri incele',
                      visible: true,
                      checkDate: '',
                      swiperText: '',
                      swiperImage: '',
                    }}
                  />
                )}
              </>
            )}

          <div className="sm:justify-between flex flex-col h-full w-full gap-4 ">
            <div className="hidden sm:flex gap-2 flex-row max-w-full text-gray-600 justify-start items-center">
              <SmartImage
                src="/payment_options.png"
                width={24}
                height={24}
                imageType="icon"
                className="h-6 w-auto"
                alt="Ödeme Seçenekleri"
              />
              <div className="text-xs font-medium mr-4">12 aya varan taksit imkanı</div>
              <SmartImage
                src="/same_day_delivery.png"
                width={24}
                height={24}
                imageType="icon"
                className="h-6 w-auto"
                alt="Aynı Gün Teslimat"
              />
              {deliveryIsCargo ? (
                <div className="text-xs font-medium mr-4">{cargo.cargoDay} Kargoda</div>
              ) : (
                <div className="text-xs font-medium mr-4">
                  {cityTitle ? `${cityTitle} içi aynı gün teslimat` : `Aynı gün teslimat`}
                </div>
              )}
              {pageState.deliveryFee === 0 && (
                <>
                  <SmartImage
                    src="/free_delivery.png"
                    width={24}
                    height={24}
                    imageType="icon"
                    className="h-6 w-auto"
                    alt="Ücretsiz Teslimat"
                    style={{ width: 'auto' }}
                  />
                  <div className="text-xs font-medium">Ücretsiz teslimat</div>
                </>
              )}
            </div>

            {deliveryIsCargo && pageState.selectedDirectButton && (
              <div className="flex flex-col gap-2 rounded-xl bg-[#ECEAE1] p-2 pl-3">
                {new Date() < new Date('2026-06-01') ? (
                  <div className="flex flex-row items-center gap-2 text-[#DF1313] border-[#aba27a] border-b-[1px] pb-3 pt-2">
                    <Calendar size={14} className="shrink-0" />
                    <div className="font-xl leading-[1rem] tracking-[-0.025em] font-poppins">
                      Siparişiniz bayram sonrası 1 Haziran&apos;da kargoya verilecektir.
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-row items-center gap-2">
                    <Calendar size={14} className="shrink-0" />
                    <div className="font-xl leading-[1rem] tracking-[-0.025em] font-poppins">
                      Kargoya verilme tarihi: {cargo.cargoDay}
                      {new Date() < new Date('2026-06-01')}
                    </div>
                  </div>
                )}
                <div className="flex flex-row items-center gap-2">
                  <Info size={14} className="shrink-0" />
                  <div className="font-xl tracking-[-0.025em] font-poppins italic">
                    <span className="text-[#DF1313]">
                      Seçtiğiniz bölgeye aynı gün teslimat yapılamamaktadır.
                    </span>{' '}
                    Siparişiniz <strong>“Kargo”</strong> ile gönderilecektir.
                  </div>
                </div>
              </div>
            )}

            {product.productTypeId === PRODUCT_TYPE.SPRAY && (
              <>
                <Input
                  ref={sprayInputRef1}
                  placeholder="Çelenk Şerit Yazısı (* zorunlu)"
                  className="h-[3rem] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm mt-2"
                  name="sprayText1"
                  onChange={(e) => setSprayText1(e.target.value)}
                  maxLength={20}
                />
                <Input
                  ref={sprayInputRef2}
                  placeholder="Çelenk Şerit Yazısı 2 (isteğe bağlı)"
                  className="h-[3rem] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                  name="sprayText2"
                  onChange={(e) => setSprayText2(e.target.value)}
                  maxLength={20}
                />
              </>
            )}

            <Button
              className="hidden sm:flex bg-[#D2F25C] hover:bg-[#D2F25C] text-hzc_beige_900 rounded-full h-[3.625rem] text-center w-1/2 text-[1.25rem] font-normal hover:scale-105 transition-all duration-300 gap-2"
              onClick={() => onOrderClick()}
              disabled={
                isVehicleDecoration
                  ? !hasDeliveryOptions || !pageState.selectedWarehouse
                  : !hasDeliveryOptions && !pageState.selectedDirectButton
              }
            >
              {pageState.orderLoading && <Spinner className="size-6 text-white" />}
              <span>{isVehicleDecoration ? APPOINTMENT_PRIMARY_CTA : 'Sipariş Ver'}</span>
            </Button>

            <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 z-50 bg-[#2B3280] flex flex-row justify-between items-center">
              <div className="flex flex-col gap-2">
                <span className="text-[.875rem] text-white font-semibold font-fraunces">
                  {activeProduct?.title || ''}
                </span>
                <span className="text-[1.25rem] text-white font-bold font-fraunces">
                  {pricesData?.[activeProduct?.id]?.price ? (
                    <>{formatNumberToPriceText(pricesData?.[activeProduct?.id]?.price || 0)} TL</>
                  ) : (
                    <>&nbsp;</>
                  )}
                </span>
              </div>
              <Button
                className="bg-[#D2F25C] rounded-[.75rem] h-[3rem] text-center text-[1rem] font-semibold hover:bg-[#D2F25C] text-hzc_beige_900 hover:scale-105 transition-all duration-300 gap-2"
                onClick={() => onOrderClick()}
                disabled={
                  isVehicleDecoration
                    ? !hasDeliveryOptions || !pageState.selectedWarehouse
                    : !hasDeliveryOptions && !pageState.selectedDirectButton
                }
              >
                {pageState.orderLoading && <Spinner className="size-5 text-hzc_beige_900" />}
                <ShoppingCart className="size-5 text-hzc_beige_900" />
                <span>{isVehicleDecoration ? APPOINTMENT_PRIMARY_CTA : 'Sipariş Ver'}</span>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Bu ürün şu anda stokta değil.</AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
}
